import type { LucideIcon } from 'lucide-react'
import {
  ActivityIcon,
  BarChart3Icon,
  BedDoubleIcon,
  BookOpenIcon,
  BoxesIcon,
  BriefcaseIcon,
  CalendarCheckIcon,
  CalendarDaysIcon,
  CircleHelpIcon,
  CodeIcon,
  CoffeeIcon,
  CreditCardIcon,
  FactoryIcon,
  HandCoinsIcon,
  HeartPulseIcon,
  LandmarkIcon,
  LayoutTemplateIcon,
  LifeBuoyIcon,
  LineChartIcon,
  MapPinnedIcon,
  PackageIcon,
  QuoteIcon,
  ReceiptIcon,
  ShoppingCartIcon,
  SparklesIcon,
  StoreIcon,
  UsersIcon,
  WalletIcon,
} from 'lucide-react'

export type NavLinkItem = {
  label: string
  description: string
  icon: LucideIcon
  href?: string
}

export type NavGroup = {
  heading: string
  /** Number of item columns this group spans in the dropdown panel. */
  columns: 1 | 2 | 3
  items: NavLinkItem[]
}

export type NavMenu = {
  label: string
  href?: string
  groups?: NavGroup[]
  footerLink?: { label: string; href?: string }
}

export const mainNav: NavMenu[] = [
  {
    label: 'Products',
    groups: [
      {
        heading: 'Products',
        columns: 3,
        items: [
          { label: 'Point of Sale', description: 'Record sales, accept payments, and issue receipts.', icon: CreditCardIcon, href: '/products/sales-pos' },
          { label: 'Inventory', description: 'Track products, materials, and stock levels.', icon: BoxesIcon, href: '/products/inventory' },
          { label: 'Purchases', description: 'Manage suppliers, purchase orders, and restocking.', icon: ShoppingCartIcon, href: '/products/purchases' },
          { label: 'Expenses', description: 'Record and organize business spending.', icon: ReceiptIcon, href: '/products/expenses' },
          { label: 'Customers', description: 'Manage contacts, balances, and customer credit.', icon: UsersIcon, href: '/products/customers' },
          { label: 'Reports & Insights', description: 'Understand sales, profit, cash flow, and performance.', icon: BarChart3Icon, href: '/products/reports' },
          { label: 'Reservations', description: 'Take table, room, and appointment bookings in one calendar.', icon: CalendarCheckIcon },
          { label: 'Venues', description: 'Manage bookings, reservations, guests, and folios.', icon: CalendarDaysIcon },
          { label: 'Business Financing', description: 'Access working capital based on your business records.', icon: LandmarkIcon, href: '/products/business-finance' },
        ],
      },
    ],
  },
  {
    label: 'Solutions',
    groups: [
      {
        heading: 'By industry',
        columns: 3,
        items: [
          { label: 'Retail', description: 'Sell faster and keep stock under control.', icon: StoreIcon, href: '/solutions/retail' },
          { label: 'Food & Beverage', description: 'Manage orders, ingredients, and daily sales.', icon: CoffeeIcon, href: '/solutions/food-beverage' },
          { label: 'Manufacturing', description: 'Track materials, production, and finished goods.', icon: FactoryIcon, href: '/solutions/manufacturing' },
          { label: 'Services', description: 'Manage clients, jobs, and payments in one place.', icon: BriefcaseIcon, href: '/solutions/services' },
          { label: 'Hospitality', description: 'Track bookings, guests, and property income.', icon: BedDoubleIcon, href: '/solutions/hospitality' },
          { label: 'Healthcare', description: 'Manage pharmacy sales, services, and stock.', icon: HeartPulseIcon, href: '/solutions/healthcare' },
        ],
      },
      {
        heading: 'By business need',
        columns: 3,
        items: [
          { label: 'Sell and get paid', description: 'Accept every payment method in one place.', icon: WalletIcon, href: '/solutions/sell-and-get-paid' },
          { label: 'Manage inventory', description: 'Know what’s in stock and when to reorder.', icon: PackageIcon, href: '/solutions/manage-inventory' },
          { label: 'Control expenses', description: 'Record every cost and see where money goes.', icon: ReceiptIcon, href: '/solutions/control-expenses' },
          { label: 'Understand performance', description: 'See sales, profit, and cash flow any day.', icon: LineChartIcon, href: '/solutions/understand-performance' },
          { label: 'Manage multiple locations', description: 'Run every branch from one workspace.', icon: MapPinnedIcon, href: '/solutions/manage-multiple-locations' },
          { label: 'Access business financing', description: 'Turn your records into working capital.', icon: HandCoinsIcon, href: '/solutions/access-business-finance' },
        ],
      },
    ],
  },
  {
    label: 'Resources',
    groups: [
      {
        heading: 'Learn',
        columns: 3,
        items: [
          { label: 'Guides & Insights', description: 'Practical advice for running and growing your business.', icon: BookOpenIcon, href: '/blog' },
          { label: 'Business Templates', description: 'Free templates for budgets, stock counts, and more.', icon: LayoutTemplateIcon, href: '/guides' },
          { label: 'Help Centre', description: 'Step-by-step answers for every Kayko feature.', icon: LifeBuoyIcon },
          { label: 'FAQs', description: 'Quick answers about plans, payments, and setup.', icon: CircleHelpIcon },
        ],
      },
      {
        heading: 'Discover',
        columns: 3,
        items: [
          { label: 'Customer Stories', description: 'How businesses across Rwanda grow with Kayko.', icon: QuoteIcon, href: '/customers' },
          { label: 'Product Updates', description: 'The latest features and improvements.', icon: SparklesIcon, href: '/whats-new' },
          { label: 'API Documentation', description: 'Connect Kayko to the tools you already use.', icon: CodeIcon },
          { label: 'System Status', description: 'Live uptime and incident updates.', icon: ActivityIcon },
        ],
      },
    ],
  },
  { label: 'Pricing', href: '/pricing' },
]
